import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {IDisposable} from '@mintdata/mint-kit/api/resources/IDisposable';
import {Popup} from '@mintdata/mint-kit/impl/components/popover/Popup';
import {PopupDismissedReason} from '@mintdata/mint-kit/impl/components/popover/PopupDismissedReason';
import {PopupKeyboardCloser} from '@mintdata/mint-kit/impl/components/popover/PopupKeyboardCloser';
import {PopupMouseCloser} from '@mintdata/mint-kit/impl/components/popover/PopupMouseCloser';
import {PopupItem, PopupItemWithIndex} from '@mintdata/mint-kit/impl/components/popover/PopupState';
import {
    IPopupManager,
    IPopupProps,
    PopupAnchor,
    PopupGroup,
    PopupId,
    PopupKind,
    PopupSize,
    PopupSource,
    SourceAnchor
} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {AnEventListener, EventBroadcaster} from '@mintdata/mint-kit/impl/events/Events';
import {ScreenUtil} from '@mintdata/mint-kit/impl/util/ScreenUtil';
import {bind} from 'bind-decorator';
import * as React from 'react';
import {ReactElement} from 'react';

export class PopupStackEvent
{
    constructor(public popupStack:Array<PopupItem>)
    {
    }
}

export type PopupStackListener = AnEventListener<PopupStackEvent>;

export class PopupManager implements IPopupManager
{
    private static readonly _SINGLETON = new PopupManager();

    private _popupStack:Array<PopupItem> = [];
    private _onPopupStackChanged = new EventBroadcaster<PopupStackEvent>();

    public static getInstance():PopupManager
    {
        return this._SINGLETON;
    }

    private constructor()
    {
        ScreenUtil.onWindowResize(() =>
        {
            this._rerenderPopovers();
        });

        new PopupMouseCloser(this as any).startListeningToMouseEvents();
        new PopupKeyboardCloser(this as any).startListeningToKeyboardEvents();
    }

    public onPopupStackChanged(listener:PopupStackListener):IDisposable
    {
        return this._onPopupStackChanged.addListener(listener);
    }

    public getPopupStack():Array<PopupItem>
    {
        return this._popupStack;
    }

    @bind
    private _rerenderPopovers():void
    {
        const popupStack = this.getPopupStack();

        if( popupStack && popupStack.length > 0 )
        {
            for( const popupItem of popupStack )
            {
                if( popupItem.kind === PopupKind.Popover )
                    popupItem.forceViewRefresh();
            }
        }
    }

    // @action
    hideTopmost(popupDismissedReason:PopupDismissedReason):void
    {
        const stack = this.getPopupStack();

        if( !stack || stack.length === 0 )
            return;

        const removedIndex = this._findFirstNotIgnoreEscIndex();

        if( removedIndex === -1 ) return;

        const removedItems:Array<PopupItem> = stack.splice(removedIndex, 1);
        const removedItem = removedItems[0];

        const onDismissedFn = removedItem.specProps.onDismissed;

        if( onDismissedFn )
            onDismissedFn(popupDismissedReason);

        this._popupStackChanged();
    }

    private _findFirstNotIgnoreEscIndex():number
    {
        const popupStack = this.getPopupStack();

        for( let i = popupStack.length - 1; i >= 0; i-- )
        {
            if( !popupStack[i].ignoreEsc )
                return i;
        }

        return -1;
    }

    private _popupStackChanged():void
    {
        //console.log('POPUP STACK changed', this._popupStack);
        this._onPopupStackChanged.fireEvent(new PopupStackEvent(this._popupStack));
    }

    // @action
    bindPopoverToHtmlElement(popupId:PopupId, popupSource:PopupSource,
                             sourceAnchor:SourceAnchor,
                             popupAnchor:PopupAnchor,
                             popupSize:PopupSize):void
    {
        const foundItem = this.findPopupItem(popupId);

        if( foundItem )
        {
            const popupItem = foundItem.popupItem;
            popupItem.popupSource = popupSource;
            popupItem.sourceAnchor = sourceAnchor;
            popupItem.popupAnchor = popupAnchor;
            popupItem.popupSize = popupSize;
        }
        else
        {
            // do nothing; ref={...} will be called again at the appropriate
            // time before the popup is rendered within the glass hierarchy of React components.
        }
    }

    registerPopup(popup:Popup, props:IPopupProps):void
    {
        this._updateStack(popup.getKind(), props);
    }

    updatePopup(popup:Popup, nextProps:IPopupProps):void
    {
        this._updateStack(popup.getKind(), nextProps);
    }

    unregisterPopup(popup:Popup):void
    {
        this._deletePopupItem(popup);
    }

    onNonPopupItemClicked():void
    {
        mk.debug(area.Popup, 'PopupController :: onNonPopupItemClicked');
        this._closePopupsFromIndexToTop(0);
    }

    onPopupItemClicked(itemWithIndex:PopupItemWithIndex):void
    {
        const stackIndex = itemWithIndex.stackIndex;
        this._closePopupsFromIndexToTop(stackIndex + 1);
    }

    closePopup(id:PopupId):void
    {
        let popupItem = this.findPopupItem(id);
        if( !popupItem )
            return;

        this._closePopupsFromIndexToTop(popupItem.stackIndex);
    }

    public closeTopmostPopup():void
    {
        this._removePopupsFromIndex(this.getPopupStack().length - 1);
    }

    closePopupsExcept(group:PopupGroup):void
    {
        const popupItems:PopupItem[] = this.getPopupStack();
        let popupStackChanged:boolean = false;

        for( let i = 0; i < popupItems.length; i++ )
        {
            if( popupItems[i].specProps.group !== group )
            {
                popupStackChanged = true;

                const removedItems:Array<PopupItem> = popupItems.splice(i, 1);
                const removedItem = removedItems[0];

                const onDismissedFn = removedItem.specProps.onDismissed;

                if( onDismissedFn )
                    onDismissedFn();
            }
        }

        if( popupStackChanged )
            this._popupStackChanged();
    }

    findPopupItem(id:PopupId):PopupItemWithIndex
    {
        const popupStack = this.getPopupStack();

        let stackIndex = 0;

        for( const item of popupStack )
        {
            if( item.id === id )
                return new PopupItemWithIndex(stackIndex, item);

            stackIndex++;
        }

        return null;
    }

    findPopupFromElement(elem:HTMLElement):PopupItemWithIndex
    {
        if( !elem )
            return null;

        if( 'hasAttribute' in elem && elem.hasAttribute('data-popupid') )
        {
            let popupId = elem.getAttribute('data-popupid') as PopupId;
            return this.findPopupItem(popupId);
        }
        else
            return this.findPopupFromElement(elem.parentElement);
    }

    findPopupFromSource(elem:HTMLElement):PopupItem
    {
        if( !elem )
            return null;

        const foundItem = this.getPopupStack().find(item => item.popupSource === elem);

        return foundItem || this.findPopupFromSource(elem.parentElement);
    }

    isElementPopupSource(elem:HTMLElement):boolean
    {
        if( !elem )
            return false;

        const found = this.getPopupStack().find(item => item.popupSource === elem);

        return !!found || this.isElementPopupSource(elem.parentElement);
    }

    private _closePopupsFromIndexToTop(fromStackIndex:number):void
    {
        const topmostModalIndex = this._findTopmostModalWindowIndex();
        const minVisibleIndex = topmostModalIndex + 1;

        // Do not allow popping items below the topmost modal Window index
        // (isModal=true Windows can only be closed via ESC key or UI-provided
        //  mechanisms specific to a each modal Window)
        if( topmostModalIndex !== -1 && fromStackIndex < minVisibleIndex )
            fromStackIndex = minVisibleIndex;

        this._removePopupsFromIndex(fromStackIndex);
    }

    // @action
    private _removePopupsFromIndex(fromStackIndex:number):void
    {
        const popupStack = this.getPopupStack();

        if( popupStack && fromStackIndex >= 0 && fromStackIndex < popupStack.length )
        {
            mk.debug(area.Popup, `AUTO closing popups, from stackIndex: ${fromStackIndex}, stack: `, popupStack);

            for( let idx = popupStack.length - 1; idx >= fromStackIndex; idx-- )
            {
                if( !popupStack[idx] ) continue;

                if( popupStack[idx].ignoreEsc ) continue;

                const popupItemToClose = popupStack[idx];
                const onDismissed = popupItemToClose.specProps.onDismissed;

                if( onDismissed && typeof onDismissed === 'function' )
                {
                    mk.debug(area.Popup, `Calling onDismissed() on ${popupItemToClose.id}`);
                    onDismissed();
                }
                popupStack.splice(idx, 1);
            }

            this._popupStackChanged();
        }
    }

    private _findTopmostModalWindowIndex():number
    {
        const popupStack = this.getPopupStack();

        for( let i = popupStack.length - 1; i >= 0; i-- )
        {
            if( popupStack[i].specProps.isModal )
                return i;
        }

        return -1;
    }

    private _updateStack(kind:PopupKind, props:IPopupProps):void
    {
        const popupId:PopupId = props.id;
        const popupItem:PopupItemWithIndex = this.findPopupItem(popupId);
        const popupHasContent:boolean = (props as any).children ? true : false;

        if( popupItem )
        {
            popupItem.popupItem.viewElement = (props as any).children;
            popupItem.popupItem.forceViewRefresh();
        }
        else if( popupHasContent )
        {
            const popupStack = this.getPopupStack();
            const newItem = this._newPopupItem(kind, props);

            popupStack.push(newItem);

            if( props.popupSource )
            {
                const {id, popupSource} = props;
                const sourceAnchor = props.sourceAnchor || SourceAnchor.LeftBottom;
                const popupAnchor = props.popupAnchor || PopupAnchor.LeftTop;
                const popupSize = props.popupSize || PopupSize.Original;

                this.bindPopoverToHtmlElement(id, popupSource, sourceAnchor, popupAnchor, popupSize);
            }

            this._popupStackChanged();
        }
    }

    private _doesStackContain(popupId:PopupId):boolean
    {
        const stack = this.getPopupStack();

        for( const item of stack )
        {
            if( item.id === popupId )
                return true;
        }

        return false;
    }

    private _deletePopupItem(popup:Popup):void
    {
        const popupItems = this.getPopupStack();

        for( let i = 0; i < popupItems.length; i++ )
        {
            if( popupItems[i].id == popup.props.id )
            {
                const removedItems:Array<PopupItem> = popupItems.splice(i, 1);
                const removedItem = removedItems[0];

                const onDismissedFn = removedItem.specProps.onDismissed;

                if( onDismissedFn )
                    onDismissedFn();

                this._popupStackChanged();
            }
        }
    }

    private _newPopupItem(kind:PopupKind, props:IPopupProps):PopupItem
    {
        const {id, ignoreEsc} = props;
        const viewElement:ReactElement<any> = (props as any).children;

        return new PopupItem(id, kind, props, viewElement, null, null, null, ignoreEsc);
    }

    public position(id:PopupId,
                    sourceAnchor:SourceAnchor = SourceAnchor.LeftBottom,
                    popupAnchor:PopupAnchor = PopupAnchor.LeftTop,
                    popupSize:PopupSize = PopupSize.Original):React.Ref<HTMLElement>
    {
        return (popupSource:HTMLElement) =>
        {
            this.bindPopoverToHtmlElement(
                id,
                popupSource,
                sourceAnchor,
                popupAnchor,
                popupSize
            );
        };
    }

    public fixedPosition(id:PopupId,
                         popupSource:PopupSource,
                         sourceAnchor:SourceAnchor = SourceAnchor.LeftBottom,
                         popupAnchor:PopupAnchor = PopupAnchor.LeftTop,
                         popupSize:PopupSize = PopupSize.Original):React.Ref<HTMLElement>
    {
        return () =>
        {
            this.bindPopoverToHtmlElement(
                id,
                popupSource,
                sourceAnchor,
                popupAnchor,
                popupSize
            );
        };
    }
}
